import {gsap, ScrollTrigger} from "../LibrariesImport";
import MatchHeight from 'matchheight';

export default class BlockListingProject {
    constructor() {
        this.mh = new MatchHeight();
        this.blocks = document.querySelectorAll('.block-listing-project');

        this.blocks.forEach((block) => {
            this.initBlock(block);
        });
    }

    initBlock(block) {
        this.filtersContainer = block.querySelector('.filters-project');
        this.filterTags = block.querySelectorAll('.filter-tag');
        this.resetButton = block.querySelector('.btn-reset');
        this.projectCards = block.querySelectorAll('[data-project-expertises], [data-project-sectors]');
        this.resultsContainer = block.querySelector('#results');
        this.returnToFilters = block.querySelector(".go-to-filters");
        this.filterStatus = block.querySelector('#filter-results-status');
        this.mh.update();

        this.activeFilters = {
            project_expertise: [],
            project_sector: []
        };

        this.filterTags.forEach(tag => {
            tag.addEventListener('click', this.handleFilterClick.bind(this));
        });

        this.resetButton.addEventListener('click', this.resetFilters.bind(this));
        this.returnToFilters.addEventListener('click', this.scrollToResults.bind(this));
        this.updateFilterState();
        this.updateProjectVisibility();
        this.toggleReturnToFilters();
    }

    toggleReturnToFilters() {
        ScrollTrigger.create({
            trigger: this.filtersContainer,
            start: "bottom 60%",
            endTrigger: document.querySelector(".footer-main"),
            end: "top bottom",
            toggleClass: {
                targets: this.returnToFilters,
                className: "active"
            }
        });
    }

    handleFilterClick(event) {
        event.preventDefault();
        const tag = event.currentTarget;
        const type = tag.getAttribute('data-filter-type');
        const value = tag.getAttribute('data-filter');

        if (!type || !value) return;

        // Met à jour l'état du filtre actif/inactif
        if (tag.classList.contains('active')) {
            tag.classList.remove('active');
            this.activeFilters[type] = this.activeFilters[type].filter(v => v !== value);
        } else {
            // Un seul filtre actif par catégorie (Comportement de filtre "radio-like")
            this.filterTags.forEach(otherTag => {
                if (otherTag.getAttribute('data-filter-type') === type && otherTag !== tag) {
                    otherTag.classList.remove('active');
                }
            });
            this.activeFilters[type] = [value];
            tag.classList.add('active');
        }

        this.updateFilterState();
        this.filterProjects();
        this.scrollToResults();
    }

    resetFilters(event) {
        if (event) event.preventDefault();

        this.activeFilters = {
            project_expertise: [],
            project_sector: []
        };

        this.filterTags.forEach(tag => {
            tag.classList.remove('active');
        });

        this.updateFilterState();
        this.filterProjects();
        this.scrollToResults();
    }

    updateFilterState() {
        const hasActiveFilters = Object.values(this.activeFilters).some(arr => arr.length > 0);

        // 1. État du bouton Réinitialiser
        if (this.resetButton) {
            this.resetButton.classList.toggle('inactive', !hasActiveFilters);
            if (hasActiveFilters) {
                this.resetButton.removeAttribute('disabled');
            } else {
                this.resetButton.setAttribute('disabled', '');
            }
        }

        // Préparation pour la logique d'impact croisé (Logique 2)
        // On doit d'abord déterminer quels tags sont RÊVABLES par les filtres ACTIFS
        // Si aucun filtre actif, on s'assure que toutes les cartes sont considérées comme visibles.
        this.projectCards.forEach(card => card.classList.remove('d-none'));

        // Stocke les types et valeurs des projets qui sont visibles APRES application des filtres actifs
        const relevantProjectTags = {
            project_expertise: new Set(),
            project_sector: new Set()
        };

        if (hasActiveFilters) {
            this.projectCards.forEach(card => {
                // Un projet est-il visible avec la combinaison des filtres actifs ?
                if (this.projectMatchesFilters(card)) {
                    // Collecte des tags de CE projet (utile pour l'autre catégorie)
                    this.collectCardTags(card, relevantProjectTags);
                }
            });
        }

        // 2. Traitement des tags (Logiques 1 & 2)
        this.filterTags.forEach(tag => {
            const type = tag.getAttribute('data-filter-type');
            const value = tag.getAttribute('data-filter');

            // Cas A : Le tag est ACTIF (on le laisse visible)
            if (tag.classList.contains('active')) {
                tag.classList.remove('inactive');
            }
            // Cas B : Il y a un filtre ACTIF dans cette MÊME catégorie (Logique 1 : forcer le choix unique)
            else if (this.activeFilters[type].length > 0) {
                tag.classList.add('inactive');
            }
            // Cas C : Il n'y a PAS de filtre actif dans cette catégorie (Catégorie non filtrée, Logique 2)
            else {
                if (!hasActiveFilters) {
                    // S'il n'y a AUCUN filtre actif du tout, aucun tag n'est inactif (tous sont disponibles)
                    tag.classList.remove('inactive');
                } else {
                    // Un filtre est actif dans l'AUTRE catégorie.
                    // On inactive ce tag s'il ne correspond à aucun des projets qui sont visibles.
                    const isRelevant = relevantProjectTags[type].has(value);
                    tag.classList.toggle('inactive', !isRelevant);
                }
            }

            tag.setAttribute('aria-pressed', tag.classList.contains('active') ? 'true' : 'false');

            if (tag.classList.contains('inactive')) {
                tag.setAttribute('disabled', '');
            } else {
                tag.removeAttribute('disabled');
            }
        });
    }

    collectCardTags(card, data) {
        const expertises = card.getAttribute('data-project-expertises');
        const sectors = card.getAttribute('data-project-sectors');

        if (expertises) {
            expertises.split(',').forEach(tag => data.project_expertise.add(tag.trim()));
        }
        if (sectors) {
            sectors.split(',').forEach(tag => data.project_sector.add(tag.trim()));
        }
    }

    projectMatchesFilters(card) {
        const cardExpertises = (card.getAttribute('data-project-expertises') || '').split(',').map(t => t.trim()).filter(Boolean);
        const cardSectors = (card.getAttribute('data-project-sectors') || '').split(',').map(t => t.trim()).filter(Boolean);

        const activeExpertise = this.activeFilters.project_expertise;
        const activeSector = this.activeFilters.project_sector;

        let matchesExpertise = activeExpertise.length === 0 || activeExpertise.some(filter => cardExpertises.includes(filter));
        let matchesSector = activeSector.length === 0 || activeSector.some(filter => cardSectors.includes(filter));

        return matchesExpertise && matchesSector;
    }

    filterProjects() {
        // 1. Fade Out général
        gsap.to(this.projectCards, {
            duration: 0.2,
            opacity: 0,
            onComplete: () => {
                // 2. Mise à jour de la visibilité après le fade out
                this.mh.update();
                this.updateProjectVisibility();

                if (typeof ScrollTrigger !== 'undefined') {
                    ScrollTrigger.refresh(true); // 'true' force un recalcul de la position du scroller
                }

                // 3. Annonce du nombre de résultats aux AT
                const visibleCards = Array.from(this.projectCards).filter(card => !card.classList.contains('d-none'));
                if (this.filterStatus) {
                    const count = visibleCards.length;
                    const word = count <= 1 ? this.filterStatus.dataset.resultSingular : this.filterStatus.dataset.resultPlural;
                    this.filterStatus.textContent = `${count} ${word}`;
                }

                // 4. Fade In des cartes visibles

                gsap.fromTo(visibleCards, {opacity: 0, y: 10}, {
                    opacity: 1,
                    y: 0,
                    duration: 0.3,
                    stagger: 0.05,
                });
            }
        });
    }

    updateProjectVisibility() {
        this.projectCards.forEach(card => {
            const isVisible = this.projectMatchesFilters(card);
            card.classList.toggle('d-none', !isVisible);
        });
    }

    scrollToResults() {
        const headerMain = document.querySelector(".header-main");
        let headerMainHeight = headerMain.offsetHeight;

        if (!this.resultsContainer) return;

        const rect = this.resultsContainer.getBoundingClientRect();

        const offsetStyle = headerMainHeight || 0;
        let offset = 0;

        if (offsetStyle) {
            offset = parseFloat(offsetStyle) || 0;
        } else {
            offset = 80;
        }

        const targetY = rect.top + window.scrollY - offset;

        window.scrollTo({
            top: targetY,
            behavior: 'smooth'
        });

        setTimeout(() => {
            document.querySelector(".header-main").classList.remove("hide");
        }, 300);
    }
}
